import { type ClientOngoingLoad, DocumentFormat, documentFormatOfFileName, documentFormatOfMimeType, loadNumberOf, PhotoType } from '@numeo/types-core'
import type { RateconRevisionAnswer } from '@numeo/types-main-web-app'
import { type MergeDocumentsResponse, ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import { rateconRevisionsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ratecon-revisions.api'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { createBoardLoadFromRatecon, findBoardLoadIds, uploadBoardDocument } from '../../api/generalAgentChatApi'

/** What the chat's file pickers offer (FR-G2): PDF, JPEG and PNG. The size limit is the board route's own. */
export const CHAT_UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'

const CHAT_UPLOAD_FORMATS: readonly DocumentFormat[] = [DocumentFormat.PDF, DocumentFormat.JPEG, DocumentFormat.PNG]

/** The document types a load upload can be filed as from the chat. A rate con is filed as the board's Docs tab files it, and
 *  a revision it opens is shown in the chat for review (see `uploadToLoad`). */
export const CHAT_DOCUMENT_TYPES = [PhotoType.RATECON, PhotoType.POD, PhotoType.BOL, PhotoType.OTHER] as const
export type ChatDocumentType = (typeof CHAT_DOCUMENT_TYPES)[number]

export const DOCUMENT_TYPE_LABEL: Record<ChatDocumentType, string> = {
    [PhotoType.RATECON]: 'rate con',
    [PhotoType.POD]: 'POD',
    [PhotoType.BOL]: 'BOL',
    [PhotoType.OTHER]: 'document',
}

/** Why a picked file is not sent, or null when it may go. */
function fileRefusal(file: File): string | null {
    const format = documentFormatOfMimeType(file.type)
    const claimed = format === DocumentFormat.UNKNOWN ? documentFormatOfFileName(file.name) : format
    return CHAT_UPLOAD_FORMATS.includes(claimed) ? null : `${file.name} is not a PDF, JPEG or PNG.`
}

/** The one board load showing this number; refused when none or more than one does. */
async function boardLoadId(loadNumber: string): Promise<string> {
    const ids = await findBoardLoadIds(loadNumber)
    if (ids.length === 1) return ids[0]
    throw new Error(
        ids.length ? `More than one load shows #${loadNumber}. Upload it from Ongoing Loads.` : `No load #${loadNumber} was found in Ongoing Loads.`
    )
}

/** The board's own words on a refused merge, when it sent any. */
const boardMessage = (response: object): string | null =>
    'message' in response && typeof response.message === 'string' && response.message ? response.message : null

/** A load upload: the load the user named and the type chosen before the file was picked (D15). */
interface LoadUpload<Type extends ChatDocumentType = ChatDocumentType> {
    loadNumber: string
    /** Known when a card in the thread named the load; otherwise found on the board by its number. */
    loadId?: string
    type: Type
    file: File
}

/**
 * Files the document on the load through the board's own upload (D17), so storage, the proof record and the automatic
 * POD/BOL check are the board's. Returns the chat message that tells the agent what was uploaded (FR-D2), or, for a rate
 * con that opened the load's revision, that revision, for the chat to show for review before anything is said.
 */
export function uploadToLoad(upload: LoadUpload<Exclude<ChatDocumentType, PhotoType.RATECON>>): Promise<string>
export function uploadToLoad(upload: LoadUpload): Promise<string | OpenedRevision>
export async function uploadToLoad({ loadNumber, loadId, type, file }: LoadUpload): Promise<string | OpenedRevision> {
    const refusal = fileRefusal(file)
    if (refusal) throw new Error(refusal)
    const id = loadId ?? (await boardLoadId(loadNumber))
    const response = await uploadBoardDocument(id, file, type)
    if (!response.success) throw new Error(boardMessage(response) || `The ${DOCUMENT_TYPE_LABEL[type]} was not uploaded.`)
    if (type === PhotoType.RATECON) return rateconFiledOnLoad(id, loadNumber, file.name, response.data)
    return `Uploaded ${DOCUMENT_TYPE_LABEL[type]} for #${loadNumber}: ${file.name}`
}

/** The load as the board has it now, to tell whether it still has no rate; a failed read only drops that part of the line. */
const boardLoad = (loadId: string): Promise<ClientOngoingLoad | undefined> => ongoingLoadsApi.getOngoingLoadById(loadId).catch(() => undefined)

/** The load's pending revision (a load has one pending at most), asked by load, so one older than the banner's newest 100 is found. */
const pendingRevisionOf = async (loadId: string): Promise<string | undefined> => {
    const { items } = await rateconRevisionsApi.list(loadId).catch(() => ({ items: [] }))
    return items.find((revision) => revision.loadId === loadId)?.id
}

/**
 * What came of a rate con filed on a load through the board's merge upload: its revision, when it opened one the chat can
 * show, or the line for the agent. The merge answer carries no revision id, so the revision is found in the board's list.
 */
async function rateconFiledOnLoad(
    loadId: string,
    loadNumber: string,
    fileName: string,
    filed: MergeDocumentsResponse['data']
): Promise<string | OpenedRevision> {
    const [load, revisionId] = await Promise.all([boardLoad(loadId), filed.revisionOpened ? pendingRevisionOf(loadId) : undefined])
    if (revisionId) return { revisionId, loadId, loadNumber, fileName, load, invoiced: Boolean(load?.brokerInvoiceId) }
    const answer = { ...filed, revisionOpened: filed.revisionOpened === true }
    // A load with no rate con takes the first one filed as its rate con; its rate still comes only from a review or the user.
    if (!answer.revisionOpened && filed.url && load?.pdfLink === filed.url)
        return withRate(`${fileName} is now the rate con of load #${loadNumber}${mismatchText(loadNumber, answer)}`, load)
    return revisionNotShownText(fileName, loadNumber, answer, load)
}

/** A rate con the agent must ask the rate of (D19): the line ends by saying so. */
const withRate = (said: string, load?: ClientOngoingLoad): string =>
    load && (load.originalRate ?? 0) <= 0 ? `${said}. The rate con shows no rate: ask me for it.` : said

/** A con the board filed as a pending revision of a load it already had: the chat shows it for review, as Ongoing Loads does. */
export interface OpenedRevision {
    revisionId: string
    loadId: string
    loadNumber: string
    fileName: string
    /** The load as it stands, before the revision is accepted or kept. */
    load?: ClientOngoingLoad
    /** The load is on an invoice, so the revision is accepted on Ongoing Loads, where the review asks what to bill. */
    invoiced?: boolean
}

/** What came of a rate con: a line for the chat, or a revision waiting for the user's choice. */
type RateconUpload = { kind: 'said'; message: string } | { kind: 'revision'; revision: OpenedRevision }

const waitsForReview = (fileName: string, loadNumber: string) => `${fileName} is a revision of load #${loadNumber}: it waits for review on Ongoing Loads`

/** What the board said of a filed con: the same fields from its process route and from its merge upload. */
type FiledRatecon = Pick<RateconRevisionAnswer, 'revisionOpened' | 'version' | 'documentIdMismatch' | 'revisionSkippedReason'>

const mismatchText = (loadNumber: string, filed: FiledRatecon): string =>
    filed.documentIdMismatch ? `. It names load ${filed.documentIdMismatch}: check it belongs to load #${loadNumber}` : ''

/** The line for a con whose load the board already had and which opened no revision the chat can show. */
function revisionNotShownText(fileName: string, loadNumber: string, revision: FiledRatecon, load?: ClientOngoingLoad): string {
    const named = `load #${loadNumber}`
    const mismatch = mismatchText(loadNumber, revision)
    if (revision.revisionOpened) return `${waitsForReview(fileName, loadNumber)}${mismatch}`
    // The board still files an invoiced load's con as a version, as its merge upload does; only rate and stops stay.
    const filedAs = revision.version ? `it is filed as rate con version ${revision.version}, ` : ''
    if (revision.revisionSkippedReason === 'invoiced')
        return `${fileName} is a revision of ${named}, which is already invoiced: ${filedAs}no revision was opened and the rate and stops are unchanged. Correct the invoice instead${mismatch}`
    if (revision.version) return withRate(`${fileName} is filed on ${named} as rate con version ${revision.version}, with nothing to review${mismatch}`, load)
    return withRate(`${fileName} is already on ${named}, nothing changed${mismatch}`, load)
}

/**
 * A rate con through the board's own upload (D16, FR-D5), which creates the load at once. A con for a load the board
 * already has opens that load's revision instead, by the board's own revision rules, and the chat shows it for review.
 * Otherwise returns the chat message that tells the agent what came of it: the load created, or the load the con
 * belongs to, and a rate the con did not show (D19), so the agent asks for it.
 */
export async function createLoadFromRatecon(file: File): Promise<RateconUpload> {
    const refusal = fileRefusal(file)
    if (refusal) throw new Error(refusal)
    const response = await createBoardLoadFromRatecon(file)
    if (!response.success) throw new Error(response.message || 'No load was created from this rate con.')
    const { load, revision } = response
    if (revision) {
        // A load with no number the board shows is named by its id, so the agent can still read it.
        const loadNumber = revision.loadNumber || (load && loadNumberOf(load)) || revision.loadId
        if (!revision.revisionOpened || !revision.revisionId) return { kind: 'said', message: revisionNotShownText(file.name, loadNumber, revision, load) }
        return {
            kind: 'revision',
            revision: { revisionId: revision.revisionId, loadId: revision.loadId, loadNumber, fileName: file.name, load, invoiced: revision.invoiced === true },
        }
    }
    if (!load) return { kind: 'said', message: `Created a load from ${file.name}` }
    const number = loadNumberOf(load)
    const named = number ? `load #${number}` : 'a load'
    // The board says whether its upsert (on the con's sourceRateConfId) inserted the load; a server that does not say yet is not guessed for.
    const said =
        response.created === true
            ? `Created ${named} from ${file.name}`
            : response.created === false
              ? `${file.name} is the rate con of ${named}, already on the board: the upload updated that load and created no new one`
              : `Uploaded ${file.name}: it is the rate con of ${named}`
    return { kind: 'said', message: withRate(said, load) }
}

/** What became of a revision shown in the chat: the user's choice, one resolved elsewhere first (`gone`), or the card
 *  closed without a choice (`left`, the revision still pending on the board). */
export type RevisionChoice = { kind: 'accepted'; load: ClientOngoingLoad; warning?: string } | { kind: 'kept' } | { kind: 'gone' } | { kind: 'left' }

/** The line telling the agent what came of a revision shown in the chat, so it reads the load again (FR-D2). */
export function revisionResultText(revision: OpenedRevision, choice: RevisionChoice): string {
    const named = `load #${revision.loadNumber}`
    switch (choice.kind) {
        case 'accepted': {
            const said = withRate(`Accepted the revision of ${named} from ${revision.fileName}: the load now carries the revised rate con`, choice.load)
            return choice.warning ? `${said}. ${choice.warning}` : said
        }
        case 'kept':
            return withRate(`Kept the original of ${named}: the revision from ${revision.fileName} was dismissed and the load is unchanged`, revision.load)
        case 'gone':
            return `The revision of ${named} from ${revision.fileName} was already resolved on Ongoing Loads`
        case 'left':
            return waitsForReview(revision.fileName, revision.loadNumber)
    }
}

/** The HTTP status a failed request was answered with, when it reached the server. */
const statusOf = (error: unknown): number | undefined => {
    const response = typeof error === 'object' && error !== null ? (error as { response?: { status?: unknown } }).response : undefined
    return typeof response?.status === 'number' ? response.status : undefined
}

/**
 * The board's own refusal, word for word (a 4xx says what to fix). A server failure carries an internal message (a database or
 * vendor error) the user cannot act on, so it is said in plain words instead.
 */
export const uploadErrorText = (error: unknown): string => {
    const status = statusOf(error)
    if (status !== undefined && status >= 500) return 'The file could not be processed right now. Try again in a minute.'
    return getUserErrorMessage(error) || 'The upload failed.'
}
